"use client";

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, MutableRefObject } from "react";
import { createPortal } from "react-dom";
import styles from "./CustomTooltip.module.scss";
interface CustomTooltipProps {
  readonly children: React.ReactElement;
  readonly content: React.ReactNode;
  readonly placement?: "top" | "bottom" | "left" | "right" | "auto";
  readonly delayShow?: number;
  readonly delayHide?: number;
  readonly disabled?: boolean;
  readonly offset?: number;
}
type TooltipChildHandlers = {
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
};
const globalScope = globalThis as typeof globalThis & {
  window?: Window;
};
const win = globalScope.window ?? null;
const isBrowser = win !== null;
export default function CustomTooltip({
  children,
  content,
  placement = "top",
  delayShow = 700,
  delayHide = 0,
  disabled = false,
  offset = 8
}: CustomTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [arrowPosition, setArrowPosition] = useState({
    x: 0,
    y: 0
  });
  const [actualPlacement, setActualPlacement] = useState<"top" | "bottom" | "left" | "right">(placement === "auto" ? "bottom" : placement);
  const childRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const childObserverRef: MutableRefObject<ResizeObserver | null> = useRef(null);
  const tooltipObserverRef: MutableRefObject<ResizeObserver | null> = useRef(null);
  const childElement = React.Children.only(children) as React.ReactElement<TooltipChildHandlers>;
  const originalRef = (childElement as React.ReactElement & {
    ref?: React.Ref<HTMLElement>;
  }).ref;
  const setRefValue = useCallback((ref: React.Ref<HTMLElement> | undefined, node: HTMLElement | null) => {
    if (!ref) return;
    if (typeof ref === "function") {
      ref(node);
      return;
    }
    try {
      (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    } catch {
      if (typeof console !== "undefined") {
        console.warn("[CustomTooltip] Failed to assign ref (may be read-only)");
      }
    }
  }, []);
  const assignChildNode = useCallback((node_0: HTMLElement | null) => {
    childRef.current = node_0;
    if (node_0 && typeof node_0.getBoundingClientRect !== "function") {
      console.warn("[CustomTooltip] Child ref is not an HTMLElement; tooltip positioning may fail.");
    }
    setRefValue(originalRef, node_0);
    if (isVisible && childObserverRef.current) {
      try {
        childObserverRef.current.disconnect();
        if (node_0) {
          childObserverRef.current.observe(node_0);
        }
      } catch {
        // Ignore observer errors
      }
    }
  }, [originalRef, setRefValue, isVisible]);
  const mergeHandlers = useCallback(<E extends React.SyntheticEvent,>(ourHandler: (event: E) => void, theirHandler?: (event: E) => void) => (event: E) => {
    ourHandler(event);
    theirHandler?.(event);
  }, []);
  const getOptimalPlacement = useCallback((childRect: DOMRect, tooltipRect: DOMRect) => {
    if (placement !== "auto") return placement;
    const padding = 8;
    const arrowSize = 8;
    const viewportHeight = win ? win.innerHeight : 0;
    const viewportWidth = win ? win.innerWidth : 0;
    const spaces = {
      top: childRect.top - padding,
      bottom: viewportHeight - childRect.bottom - padding,
      left: childRect.left - padding,
      right: viewportWidth - childRect.right - padding
    };
    const requiredVerticalSpace = tooltipRect.height + offset + arrowSize;
    const requiredHorizontalSpace = tooltipRect.width + offset + arrowSize;
    const placements = [{
      name: "top",
      space: spaces.top,
      required: requiredVerticalSpace
    }, {
      name: "bottom",
      space: spaces.bottom,
      required: requiredVerticalSpace
    }, {
      name: "right",
      space: spaces.right,
      required: requiredHorizontalSpace
    }, {
      name: "left",
      space: spaces.left,
      required: requiredHorizontalSpace
    }] as const;
    const validPlacement = placements.find(p => p.space >= p.required);
    return validPlacement ? validPlacement.name : "bottom";
  }, [placement, offset]);
  const calculateInitialPosition = useCallback((childRect_0: DOMRect, tooltipRect_0: DOMRect, targetPlacement: string) => {
    let x = 0;
    let y = 0;
    switch (targetPlacement) {
      case "top":
        x = childRect_0.left;
        y = childRect_0.top - tooltipRect_0.height - offset;
        break;
      case "bottom":
        x = childRect_0.left;
        y = childRect_0.bottom + offset;
        break;
      case "left":
        x = childRect_0.left - tooltipRect_0.width - offset;
        y = childRect_0.top + (childRect_0.height - tooltipRect_0.height) / 2;
        break;
      case "right":
        x = childRect_0.right + offset;
        y = childRect_0.top + (childRect_0.height - tooltipRect_0.height) / 2;
        break;
    }
    return {
      x,
      y
    };
  }, [offset]);
  const adjustPositionForViewport = useCallback((position_0: {
    x: number;
    y: number;
  }, childRect_1: DOMRect, tooltipRect_1: DOMRect, targetPlacement_0: string) => {
    const padding_0 = 8;
    const viewportHeight_0 = win ? win.innerHeight : 0;
    const viewportWidth_0 = win ? win.innerWidth : 0;
    let {
      x: x_0,
      y: y_0
    } = position_0;
    let finalPlacement = targetPlacement_0;

    // Keep tooltip within viewport bounds horizontally
    const maxX = viewportWidth_0 - tooltipRect_1.width - padding_0;
    x_0 = Math.max(padding_0, Math.min(x_0, maxX));

    // Adjust vertical position to prevent overlap
    if (targetPlacement_0 === "top" && y_0 < padding_0) {
      finalPlacement = "bottom";
      y_0 = childRect_1.bottom + offset;
    } else if (targetPlacement_0 === "bottom" && y_0 + tooltipRect_1.height > viewportHeight_0 - padding_0) {
      finalPlacement = "top";
      y_0 = childRect_1.top - tooltipRect_1.height - offset;
    } else if (targetPlacement_0 === "left" && x_0 < padding_0) {
      finalPlacement = "right";
      x_0 = childRect_1.right + offset;
    } else if (targetPlacement_0 === "right" && x_0 + tooltipRect_1.width > viewportWidth_0 - padding_0) {
      finalPlacement = "left";
      x_0 = childRect_1.left - tooltipRect_1.width - offset;
    }
    return {
      x: x_0,
      y: y_0,
      finalPlacement
    };
  }, [offset]);
  const calculateArrowPosition = useCallback((position_1: {
    x: number;
    y: number;
  }, childRect_2: DOMRect, tooltipRect_2: DOMRect, finalPlacement_0: string) => {
    let arrowX = 0;
    let arrowY = 0;
    if (finalPlacement_0 === "top" || finalPlacement_0 === "bottom") {
      const childCenterX = childRect_2.left + childRect_2.width / 2;
      arrowX = childCenterX - position_1.x;
      arrowX = Math.max(16, Math.min(arrowX, tooltipRect_2.width - 16));
    } else if (finalPlacement_0 === "left" || finalPlacement_0 === "right") {
      const childCenterY = childRect_2.top + childRect_2.height / 2;
      arrowY = childCenterY - position_1.y;
      arrowY = Math.max(16, Math.min(arrowY, tooltipRect_2.height - 16));
    }
    return {
      x: arrowX,
      y: arrowY
    };
  }, []);
  const calculatePosition = useCallback(() => {
    if (!childRef.current || !tooltipRef.current) return;
    if (!isBrowser) return;
    const childRect_3 = childRef.current.getBoundingClientRect();
    const tooltipRect_3 = tooltipRef.current.getBoundingClientRect();
    const targetPlacement_1 = getOptimalPlacement(childRect_3, tooltipRect_3);
    const initialPosition = calculateInitialPosition(childRect_3, tooltipRect_3, targetPlacement_1);
    const adjustedPosition = adjustPositionForViewport(initialPosition, childRect_3, tooltipRect_3, targetPlacement_1);
    const arrowPos = calculateArrowPosition(adjustedPosition, childRect_3, tooltipRect_3, adjustedPosition.finalPlacement);
    setPosition({
      x: adjustedPosition.x,
      y: adjustedPosition.y
    });
    setArrowPosition(arrowPos);
    setActualPlacement(adjustedPosition.finalPlacement as "top" | "bottom" | "left" | "right");
  }, [getOptimalPlacement, calculateInitialPosition, adjustPositionForViewport, calculateArrowPosition]);
  const show = useCallback(() => {
    if (disabled) return;
    if (hideTimer.current !== undefined) {
      clearTimeout(hideTimer.current);
    }
    showTimer.current = setTimeout(() => {
      setIsVisible(true);
    }, delayShow);
  }, [disabled, delayShow]);
  const hide = useCallback(() => {
    if (showTimer.current !== undefined) {
      clearTimeout(showTimer.current);
    }
    hideTimer.current = setTimeout(() => setIsVisible(false), delayHide);
  }, [delayHide]);
  useLayoutEffect(() => {
    if (!isVisible) return;
    const frame = requestAnimationFrame(() => {
      calculatePosition();
    });
    return () => cancelAnimationFrame(frame);
  }, [isVisible, calculatePosition]);
  useEffect(() => {
    if (!isVisible) return;
    if (!tooltipRef.current) return;
    if (!isBrowser) return;
    if (typeof ResizeObserver === "undefined") {
      calculatePosition();
      return;
    }
    const observer = new ResizeObserver(() => {
      calculatePosition();
    });
    tooltipObserverRef.current = observer;
    observer.observe(tooltipRef.current);
    return () => {
      observer.disconnect();
      tooltipObserverRef.current = null;
    };
  }, [isVisible, calculatePosition]);
  useEffect(() => {
    if (!isVisible) return;
    if (!childRef.current) return;
    if (!isBrowser) return;
    if (typeof ResizeObserver === "undefined") {
      calculatePosition();
      return;
    }
    const observer_0 = new ResizeObserver(() => {
      calculatePosition();
    });
    childObserverRef.current = observer_0;
    observer_0.observe(childRef.current);
    return () => {
      observer_0.disconnect();
      childObserverRef.current = null;
    };
  }, [isVisible, calculatePosition]);
  useEffect(() => {
    if (!isVisible) return;
    if (!isBrowser) return;
    let rafId: number | null = null;
    const handleReposition = () => {
      if (rafId !== null) return;
      rafId = win.requestAnimationFrame(() => {
        rafId = null;
        calculatePosition();
      });
    };
    win.addEventListener("resize", handleReposition);
    win.addEventListener("scroll", handleReposition, true);
    return () => {
      win.removeEventListener("resize", handleReposition);
      win.removeEventListener("scroll", handleReposition, true);
      if (rafId !== null) {
        win.cancelAnimationFrame(rafId);
      }
    };
  }, [isVisible, calculatePosition]);
  useEffect(() => {
    return () => {
      if (showTimer.current !== undefined) {
        clearTimeout(showTimer.current);
      }
      if (hideTimer.current !== undefined) {
        clearTimeout(hideTimer.current);
      }
      childObserverRef.current?.disconnect();
      tooltipObserverRef.current?.disconnect();
    };
  }, []);
  const clonedChild = React.cloneElement(childElement, {
    ref: assignChildNode,
    onMouseEnter: mergeHandlers<React.MouseEvent<HTMLElement>>(() => {
      show();
    }, childElement.props.onMouseEnter),
    onMouseLeave: mergeHandlers<React.MouseEvent<HTMLElement>>(() => {
      hide();
    }, childElement.props.onMouseLeave),
    onFocus: mergeHandlers<React.FocusEvent<HTMLElement>>(() => {
      show();
    }, childElement.props.onFocus),
    onBlur: mergeHandlers<React.FocusEvent<HTMLElement>>(() => {
      hide();
    }, childElement.props.onBlur)
  } as React.Attributes & TooltipChildHandlers);
  return <>
      {clonedChild}
      {isVisible && typeof document !== "undefined" && createPortal(<div ref={tooltipRef} role="tooltip" className={`${styles.tooltip} ${styles["tooltip--" + actualPlacement]}`} style={{
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
      zIndex: 999999,
      pointerEvents: 'none'
    }}>
          <div className={styles.tooltipContent}>
            {content}
          </div>
          <div className={`${styles.tooltipArrow} ${styles["tooltipArrow--" + actualPlacement]}`} style={{
        ...((actualPlacement === "top" || actualPlacement === "bottom") && {
          left: `${arrowPosition.x}px`,
          transform: 'translateX(-50%)'
        }),
        ...((actualPlacement === "left" || actualPlacement === "right") && {
          top: `${arrowPosition.y}px`,
          transform: 'translateY(-50%)'
        })
      }} />
        </div>, document.body)}
    </>;
}